import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import Header from '../components/Header';

const API_BASE = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';

function adminRequest(method, path, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('ts_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(`${API_BASE}${path}`, { method, headers, body: body ? JSON.stringify(body) : null })
    .then(async res => { const data = await res.json(); if (!res.ok) throw data; return data; });
}

function Admin() {
  const { vendor, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!vendor) { navigate('/vendor/login'); return; }
    loadVendors();
  }, [vendor, authLoading, filter]);

  async function loadVendors() {
    try {
      const data = await adminRequest('GET', `/admin/vendors?status=${filter}`);
      setVendors(data.vendors || []);
    } catch (err) {
      setError(err.error || 'Access denied');
    }
  }

  async function handleApprove(vendorId) {
    await adminRequest('PUT', `/admin/vendors/${vendorId}/approve`);
    loadVendors();
  }

  async function handleReject(vendorId) {
    const reason = prompt('Rejection reason (optional):');
    await adminRequest('PUT', `/admin/vendors/${vendorId}/reject`, { reason });
    loadVendors();
  }

  async function handleSuspend(vendorId) {
    const reason = prompt('Suspension reason:');
    if (reason === null) return;
    await adminRequest('PUT', `/admin/vendors/${vendorId}/suspend`, { reason });
    loadVendors();
  }

  async function handleBan(vendorId) {
    if (!window.confirm('⚠️ BAN this vendor? This will permanently delete all their items, blueprints, and booth data. This cannot be undone.')) return;
    await adminRequest('PUT', `/admin/vendors/${vendorId}/ban`);
    loadVendors();
  }

  async function handleUnsuspend(vendorId) {
    await adminRequest('PUT', `/admin/vendors/${vendorId}/approve`);
    loadVendors();
  }

  return (
    <div className="dashboard-page">
      <Header />
      <div className="commissions-content">
        <div className="dashboard-header">
          <h2>⚙️ Admin Dashboard</h2>
        </div>

        <div className="dashboard-tabs">
          <button className={`tab ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Pending</button>
          <button className={`tab ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Active</button>
          <button className={`tab ${filter === 'suspended' ? 'active' : ''}`} onClick={() => setFilter('suspended')}>Suspended</button>
          <button className={`tab ${filter === 'banned' ? 'active' : ''}`} onClick={() => setFilter('banned')}>Banned</button>
          <button className={`tab ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>Rejected</button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <div className="admin-vendor-list">
          {vendors.length === 0 && <p className="empty-state">No {filter} vendors.</p>}
          {vendors.map(v => (
            <div className="admin-vendor-card" key={v.vendorId}>
              <div className="admin-vendor-info">
                {v.profileImageUrl && <img src={v.profileImageUrl} alt="" className="admin-vendor-logo" />}
                <div>
                  <h3>{v.businessName}</h3>
                  <p>{v.email}</p>
                  <p className="admin-vendor-meta">{v.specialty || 'No specialty'} • Joined {new Date(v.createdAt * 1000).toLocaleDateString()}</p>
                  <p className="admin-vendor-meta">Stripe: {v.stripeAccountId ? '✓ Connected' : '✗ Not connected'}</p>
                </div>
              </div>
              {filter === 'pending' && (
                <div className="admin-actions">
                  <button className="btn-primary" onClick={() => handleApprove(v.vendorId)}>Approve</button>
                  <button className="btn-delete" onClick={() => handleReject(v.vendorId)}>Reject</button>
                </div>
              )}
              {filter === 'active' && (
                <div className="admin-actions">
                  <button className="btn-secondary" onClick={() => handleSuspend(v.vendorId)}>Suspend</button>
                  <button className="btn-delete" onClick={() => handleBan(v.vendorId)}>Ban</button>
                </div>
              )}
              {filter === 'suspended' && (
                <div className="admin-actions">
                  <button className="btn-primary" onClick={() => handleUnsuspend(v.vendorId)}>Unsuspend</button>
                  <button className="btn-delete" onClick={() => handleBan(v.vendorId)}>Ban</button>
                  {v.suspendReason && <p className="admin-vendor-meta">Reason: {v.suspendReason}</p>}
                </div>
              )}
              {filter === 'rejected' && v.rejectionReason && (
                <p className="admin-vendor-meta">Reason: {v.rejectionReason}</p>
              )}
            </div>
          ))}
        </div>

        <RefundManager />
      </div>
    </div>
  );
}

function RefundManager() {
  const [refunds, setRefunds] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  async function loadRefunds() {
    try {
      const data = await adminRequest('GET', '/admin/refunds');
      setRefunds(data.refunds || []);
      setLoaded(true);
    } catch (e) { setLoaded(true); }
  }

  async function handleApprove(refundId) {
    if (!window.confirm('Approve this refund? Funds will be returned to the customer.')) return;
    try {
      await adminRequest('PUT', `/admin/refunds/${refundId}/approve`);
      setRefunds(prev => prev.map(r => r.refundId === refundId ? { ...r, status: 'refunded' } : r));
    } catch (e) { alert(e.error || 'Failed'); }
  }

  async function handleDeny(refundId) {
    const reason = prompt('Reason for denial:');
    if (reason === null) return;
    try {
      await adminRequest('PUT', `/admin/refunds/${refundId}/deny`, { reason });
      setRefunds(prev => prev.map(r => r.refundId === refundId ? { ...r, status: 'denied' } : r));
    } catch (e) { alert(e.error || 'Failed'); }
  }

  return (
    <div style={{marginTop:'2rem'}}>
      <h3>Refund Requests</h3>
      {!loaded && <button className="btn-secondary" onClick={loadRefunds}>Load Refund Requests</button>}
      {loaded && refunds.length === 0 && <p className="empty-state">No pending refund requests.</p>}
      {refunds.map(r => (
        <div className="admin-vendor-card" key={r.refundId}>
          <div className="admin-vendor-info">
            <div>
              <h3>{r.itemName || 'Order'}</h3>
              <p className="admin-vendor-meta">Amount: ${(r.amount || 0) / 100} • Status: {r.status}</p>
              <p className="admin-vendor-meta">Reason: {r.reason || 'None given'}</p>
              <p className="admin-vendor-meta">Requested: {new Date((r.createdAt || 0) * 1000).toLocaleDateString()}</p>
            </div>
          </div>
          {r.status === 'pending_review' && (
            <div className="admin-actions">
              <button className="btn-primary" onClick={() => handleApprove(r.refundId)}>Approve Refund</button>
              <button className="btn-delete" onClick={() => handleDeny(r.refundId)}>Deny</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Admin;
